// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { ChunkParser, StreamCallback } from "../types"

/**
 * Coerce an untrusted field to a string, or undefined if it is not one.
 *
 * Deliberately NOT `String(x)`: on an object that yields the literal
 * "[object Object]", which would surface as a chart title or — worse — as a URL
 * the card tries to load. A wrong-typed field should drop out, not become junk.
 */
function asText(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined
}

/**
 * Map an `artifact` frame to a StreamEvent.
 *
 * Extracted from the switch so the parser stays under the complexity budget, and
 * so the drop rules live in one readable place.
 */
function emitArtifact(json: Record<string, unknown>, callback: StreamCallback): void {
  // `runId` keys the artifact for click-time presign refresh, so an event without
  // one cannot be addressed downstream — drop it rather than emit a broken card.
  const runId = asText(json.run_id)
  if (!runId) return
  const url = asText(json.url)
  // Renderable only if there is something to render: live `data` OR a PNG.
  if (json.data === undefined && !url) return
  callback({
    type: "artifact",
    kind: asText(json.kind) ?? "chart",
    fmt: asText(json.fmt) ?? "png",
    // Both spread conditionally on purpose: `url` is absent when the PNG is
    // skipped or its upload failed, and the UI must still render from `data`.
    ...(url ? { url } : {}),
    ...(json.data !== undefined ? { data: json.data } : {}),
    name: asText(json.name) ?? "grafica",
    bytes: typeof json.bytes === "number" ? json.bytes : undefined,
    runId,
    backend: asText(json.backend),
  })
}

/**
 * Parser for the OCESA promotores agent (src/agents/promotores/app.py).
 *
 * That agent does NOT emit raw Strands events — it wraps every SSE frame as
 * `{"type": <kind>, "data": <payload>}`, so it needs its own parser instead of
 * the generic Strands one:
 *   - "output"    → assistant answer text (data is a string chunk)
 *   - "reasoning" → thinking / tool-progress status, routed to the collapsible
 *                   "Razonamiento" panel so it stays out of the answer
 *   - "error"     → error banner (data is an object carrying .message)
 *   - "artifact"  → chart produced by the sandbox. `data` (columns/rows + chart
 *                   spec) is the PRIMARY representation the UI renders; `url` is
 *                   an OPTIONAL presigned PNG kept as a downloadable/durable
 *                   alternative and expected to be retired once live rendering
 *                   lands. An artifact with `data` and no `url` is VALID.
 *   - "metrics" / "retry" / "result" → no UI mapping today; ignored
 */
export const parseOcesaChunk: ChunkParser = (line, callback) => {
  if (!line.startsWith("data: ")) return

  const data = line.substring(6).trim()
  if (!data) return

  try {
    const json = JSON.parse(data)

    switch (json.type) {
      case "output":
        if (typeof json.data === "string" && json.data) {
          callback({ type: "text", content: json.data })
        }
        return

      case "reasoning":
        if (typeof json.data === "string" && json.data) {
          callback({ type: "reasoning", content: json.data })
        }
        return

      case "error": {
        const d = json.data
        const message =
          d && typeof d === "object" ? String(d.message ?? "Error") : String(d ?? "Error")
        callback({ type: "error", message })
        return
      }

      case "artifact":
        emitArtifact(json, callback)
        return

      case "attachment_error": {
        // El runtime descartó un adjunto. NO es un `error` de turno: la respuesta
        // sigue llegando. Se emite aparte para que la UI la muestre como aviso y no
        // como fallo, pero sin dejarla pasar en silencio.
        const rejected = Array.isArray(json.rejected)
          ? json.rejected
              .filter((r: unknown): r is Record<string, unknown> => !!r && typeof r === "object")
              .map((r: Record<string, unknown>) => ({
                filename: asText(r.filename) || "archivo",
                reason: asText(r.reason) || "motivo desconocido",
              }))
          : []
        const message = asText(json.data) || "No se pudo usar un archivo adjunto."
        callback({ type: "attachment_error", message, rejected })
        return
      }

      default:
        return
    }
  } catch {
    console.debug("Failed to parse OCESA event:", data)
  }
}
